import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { DebugWindow, type DebugTab } from './DebugWindow';
import { teamReporterName } from './teamReporter';

/**
 * A ticket's debugging window opened from its card (TOADO-945), over the page
 * the card is on: the board, an inbox list, search. Session and DevTools are
 * its two tabs (24 Oct), so a Session row's link to the console, a request or
 * an error opens DevTools here rather than leaving the page. The window builds
 * from the full ticket, so it loads it under the ticket page's own query key,
 * which makes opening the ticket afterwards instant.
 */
export function CardSession({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const [tab, setTab] = useState<DebugTab>('session');
  // Each card's window opens on its visit.
  useEffect(() => { if (ticketId) setTab('session'); }, [ticketId]);
  const ticket = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => api.getTicket(ticketId!),
    enabled: !!ticketId,
  });
  // The ticket page's own members query, for who filed it.
  const { currentCompanyId } = useAuth();
  const members = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!ticketId && !!currentCompanyId,
  });
  return (
    <DebugWindow
      ticket={ticket.data}
      open={!!ticketId}
      onClose={onClose}
      tab={tab}
      onTab={setTab}
      teamReporter={teamReporterName(ticket.data, members.data)}
      {...(ticket.isPending ? { state: 'loading' as const } : ticket.isError ? { state: 'error' as const } : {})}
    />
  );
}
